/**
 * The bell, and the panel under it — one copy, both apps.
 *
 * It was written inline in the office header, and when Rowted Admin got the
 * same header shape it got no bell at all: "the admin version of the user menu
 * drop down doesn't have notifications, which will be in admin as well". A
 * second copy would have answered that and then drifted, because the badge's
 * geometry (a 16px pill, 1.5px of surface around it so it reads against the
 * icon) is the kind of detail nobody re-derives the same way twice.
 *
 * **The office side has rows now** (`docs/proposal.md` §12.3, migration 0117):
 * `user_notifications` is what `notifyUser()` writes and `/me/notifications`
 * is what this reads. Rowted Admin's bell is still the placeholder, and that
 * is not an oversight — `user_notifications` is a *tenant* table and the
 * console's client prefixes `/api/v1/platform`, so there is nothing for it to
 * read until the platform side raises notifications of its own. A sentence
 * saying where the rows will come from is better than a feed with invented
 * ones in it.
 */
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Popover } from '../components/overlays.js'
import { IconBell } from './icons.js'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { formatRelative } from '../lib/format.js'
import { VisitGlyph } from '../visit/glyphs.js'

export const NotificationBell = forwardRef<HTMLDivElement, {
  count: number
  open: boolean
  onToggle: () => void
}>(function NotificationBell({ count, open, onToggle }, ref) {
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-label={`Notifications${count > 0 ? `, ${count} unread` : ''}`}
        aria-expanded={open}
        className={cn('relative inline-flex h-9 w-9 items-center justify-center',
          'rounded-field text-text-muted hover:bg-hover')}
      >
        <IconBell size={19} />
        {count > 0 && (
          <i className={cn('absolute right-1.5 top-1.5 inline-flex h-4 min-w-4 items-center',
            'justify-center rounded-full border-[1.5px] border-surface bg-danger px-1',
            'text-[9.5px] font-bold not-italic text-white')}
          >
            {count}
          </i>
        )}
      </button>
    </div>
  )
})

export interface UserNotification {
  id: string
  kind: string
  event: string
  title: string
  body: string
  link: string | null
  createdAt: string
  readAt: string | null
}

/**
 * The bell's own data, fetched once by whichever frame draws it.
 *
 * It polls rather than subscribing, at a minute — `notifyUser` publishes an
 * SSE event, and wiring the bell to the stream is the obvious next step, but
 * a minute is well inside how long somebody takes to notice a badge and a
 * poll cannot miss an event because the stream dropped. Refetched on open,
 * because the one moment accuracy matters is the moment somebody looks.
 */
export function useNotifications(enabled: boolean): {
  unread: number
  items: UserNotification[]
  refresh: () => void
  markRead: (id?: string) => void
} {
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState<UserNotification[]>([])

  const refresh = useCallback(() => {
    if (!enabled) return
    void request<{ unread: number; items: UserNotification[] }>('/me/notifications')
      .then((payload) => { setUnread(payload.unread); setItems(payload.items) })
      /* A bell that cannot load is a bell with nothing in it, not an error
       * card across the top of somebody's screen. */
      .catch(() => { /* not worth a word */ })
  }, [enabled])

  useEffect(() => {
    refresh()
    if (!enabled) return undefined
    const timer = window.setInterval(refresh, 60_000)
    return () => window.clearInterval(timer)
  }, [refresh, enabled])

  const markRead = useCallback((id?: string) => {
    /* Optimistic, because the badge is the whole point and a round trip to
     * clear it reads as a control that did not work. */
    setUnread((was) => (id ? Math.max(0, was - 1) : 0))
    setItems((was) => was.map((one) => (id && one.id !== id
      ? one
      : { ...one, readAt: one.readAt ?? new Date().toISOString() })))
    void request<{ unread: number }>('/me/notifications/read',
      { method: 'POST', body: id ? { id } : {} })
      .then((payload) => setUnread(payload.unread))
      .catch(() => { /* the next poll fixes it */ })
  }, [])

  return { unread, items, refresh, markRead }
}

export function NotificationsPanel({
  open, onClose, anchor, items, onRead, onOpenLink,
}: {
  open: boolean
  onClose: () => void
  anchor: HTMLElement | null
  /**
   * Absent on the side that has none to show (Rowted Admin), which draws the
   * sentence instead — a placeholder is honest where invented rows are not.
   */
  items?: UserNotification[]
  onRead?: (id?: string) => void
  onOpenLink?: (link: string) => void
}) {
  const unread = (items ?? []).filter((one) => one.readAt === null).length

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchor={anchor}
      align="right"
      width={380}
      label="Notifications"
    >
      {!items
        ? (
          <div className="px-3 py-8 text-center text-cell text-text-muted">
            Notifications arrive with the modules that raise them.
          </div>
        )
        : (
          <>
            <div className="flex h-10 items-center justify-between border-b border-line-faint
                            px-3">
              <span className="text-cell font-bold text-text">Notifications</span>
              {unread > 0 && onRead && (
                <button
                  type="button"
                  onClick={() => onRead()}
                  className="flex h-8 items-center rounded-field px-1.5 text-sub
                             font-semibold text-accent"
                >
                  Mark all read
                </button>
              )}
            </div>

            {items.length === 0
              ? (
                <div className="px-3 py-8 text-center text-cell text-text-muted">
                  Nothing yet.
                </div>
              )
              : (
                <ul className="m-0 max-h-menu list-none overflow-y-auto p-0">
                  {items.map((one) => (EXCEPTION_EVENTS.has(one.event)
                    ? (
                      <ExceptionRow
                        key={one.id}
                        one={one}
                        onPress={() => {
                          onRead?.(one.id)
                          if (one.link) onOpenLink?.(one.link)
                          onClose()
                        }}
                      />
                    )
                    : (
                    <li key={one.id} className="border-b border-line-faint last:border-b-0">
                      <button
                        type="button"
                        onClick={() => {
                          onRead?.(one.id)
                          if (one.link) onOpenLink?.(one.link)
                          onClose()
                        }}
                        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left
                                   hover:bg-hover"
                      >
                        {/* Unread is a dot, not a tint: a panel of tinted rows
                            that fade one at a time is a panel that looks
                            broken half the time. */}
                        <span aria-hidden className={cn('mt-[7px] h-[7px] w-[7px] shrink-0',
                          'rounded-full', one.readAt === null ? 'bg-accent' : 'bg-transparent')}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-cell font-semibold text-text">
                            {one.title}
                          </span>
                          {one.body && (
                            <span className="block truncate text-sub text-text-muted">
                              {one.body}
                            </span>
                          )}
                          <span className="block text-sub text-text-faint">
                            {formatRelative(one.createdAt)}
                          </span>
                        </span>
                      </button>
                    </li>
                    )))}
                </ul>
              )}
          </>
        )}
    </Popover>
  )
}

/**
 * The two notifications that say a quote's visits are not all there —
 * `docs/visit-page.md` §26.3 and §26.6, Board 17 D: the daily check's
 * finding and a refused approval on the customer's portal. Drawn as the board
 * draws them: the red warning tile, the title, the body that already carries
 * its own time (*… · today 2:00 AM*), and the red dot on the right while unread.
 */
const EXCEPTION_EVENTS: ReadonlySet<string> = new Set(['visit_check_missing', 'approval_failed'])

function ExceptionRow({ one, onPress }: { one: UserNotification; onPress: () => void }) {
  const unread = one.readAt === null
  return (
    <li data-as-drawn="vp-17 D" className="border-b border-line-faint last:border-b-0">
      <button
        type="button"
        onClick={onPress}
        className={cn('flex w-full gap-2.5 px-3.5 py-[11px] text-left leading-[normal] tabular-nums hover:bg-hover',
          unread && 'bg-danger-bg/20')}
      >
        <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center
                         rounded-control bg-danger-bg text-danger">
          <VisitGlyph name="warn" size={15} stroke={2} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <b className="text-[13px] text-text">{one.title}</b>
          {one.body && (
            <span className="mt-0.5 text-[11.5px] leading-[1.4] text-text-muted">{one.body}</span>
          )}
        </span>
        {unread && (
          <i aria-hidden className="mt-[5px] block h-2 w-2 shrink-0 rounded-full bg-danger" />
        )}
      </button>
    </li>
  )
}
